import {
	PopOver,
	PopOverSceneItemData,
	Orientation,
	Platform,
	DeviceType,
	WriteDirection,
	PopOverType,
	TranslationsKey,
	RangeSettings,
	IModuleSwiperItem,
} from 'sofa-reborn-react';
import { ConnectionManager, SceneObject } from '@ikea-aoa/component-view-3d';
import { MeshRenderer } from '@ikea-aoa/elements-3d';
import { SceneLogic } from './SceneLogic';
import { MeasurementsManager } from './UiController/MeasurementsManager';
import { SelectionUi } from './UiController/SelectionUi';
import { View3dElement } from '@ikea-aoa/component-view-3d';

type Props = {
	orientation: Orientation;
	platform: Platform;
	deviceType: DeviceType;
	unfinishedSection?: PopOver;
	showMeasurements?: boolean;
	showSelectAll?: boolean;
	useMetricMeasures: boolean | null;
	writeDirection?: WriteDirection;
	rangeSettings: RangeSettings;
	specificItems: Map<string, SceneObject>;
	moduleSwiperItems: Array<IModuleSwiperItem>;

	setUnfinishedModule: (value: PopOverSceneItemData) => void;
	selectSceneItem: (item: PopOverSceneItemData | null) => void;
	showPopOver: (value: PopOver) => void;
	hidePopOver: (value: PopOver) => void;
	setShowSelectAll: (value: boolean) => void;
	setShowMeasurements: (value: boolean, track: boolean) => void;
	setMeasurements: (value: string) => void;
};

const ITEM_POPOVER_OFFSET = {
	default: 3,
	headrest: 1,
	mobilePortrait: 1.5,
	mobileLandscape: -0.75,
};

export class UiController {
	private sceneItems: Map<string, SceneObject>;
	private connectionManager: ConnectionManager;
	private selectionUi: SelectionUi;
	private measurementsManager: MeasurementsManager;
	private moduleName: string | undefined;

	constructor(private view3d: View3dElement, private logic: SceneLogic, private props: Props) {
		this.sceneItems = logic.sceneItems;
		this.connectionManager = logic.connectionManager;

		this.selectionUi = new SelectionUi(this.sceneItems);
		this.measurementsManager = new MeasurementsManager(view3d, this.props);
		this.measurementsManager.create();
	}

	public updateProps(props: Props): void {
		this.props = props;
	}

	public selectSpecificItems(sceneItems: Map<string, SceneObject>): void {
		this.selectionUi.setSelection(sceneItems);
	}

	public selectItem(sceneObject: SceneObject): void {
		if (sceneObject.articleId.includes('backrest')) {
			// Backrest selected, select its parent instead, if not a chaise
			const parentSceneObject = this.getParentSceneObject(sceneObject);
			if (parentSceneObject) {
				const isChaise = this.connectionManager.getFamily(parentSceneObject.articleId) === 'CHAISE';
				if (!isChaise) {
					this.setPopOverSceneItemData(parentSceneObject);
					this.selectionUi.setSelection(parentSceneObject.id);
				} else {
					this.setPopOverSceneItemData(sceneObject);
					this.selectionUi.setSelection(sceneObject.id);
				}
			}
		} else {
			this.setPopOverSceneItemData(sceneObject);
			this.selectionUi.setSelection(sceneObject.id);
		}
	}

	public selectAll(): void {
		this.selectionUi.setSelection(this.sceneItems);
	}

	public unselect(): void {
		this.selectionUi.clearSelection();
	}

	public AddMeasurements(): void {
		const camera = this.view3d.getCamera();
		const mainCamera = camera?.getCamera('Camera');
		mainCamera?.onViewMatrixChangedObservable.add(() => {
			if (!this.props.showMeasurements) {
				return;
			}
			this.measurementsManager.show(camera);
		});
	}

	public updateTotalMeasurements(): void {
		const ext = this.logic.getPositionsExtends(this.sceneItems, false);
		let fixedHeight: string | null = null;

		if (this.props.rangeSettings.fixedHeightValues) {
			const fixedHeightValues = this.props.rangeSettings.fixedHeightValues;
			let fixedHeightValue = 0;
			const sceneItemsArr = Array.from(this.sceneItems.values());

			sceneItemsArr.forEach((sceneItem: SceneObject) => {
				const model = this.connectionManager.getModelData(sceneItem.articleId);
				const sceneItemFixedHeight = fixedHeightValues[model ? model.modelId : ''];

				if (sceneItemFixedHeight > fixedHeightValue) {
					fixedHeightValue = sceneItemFixedHeight;
				}
			});

			fixedHeight = fixedHeightValue.toString();
		}

		this.measurementsManager.update(ext, fixedHeight);
		if (!this.props.showMeasurements) {
			this.measurementsManager.hide();
		} else {
			const measurements =
				this.sceneItems.size === 0 ? '' : this.measurementsManager.getMobileMeasurements();
			this.props.setMeasurements(measurements);
		}
	}

	public showPopOver(type: PopOverType, item: SceneObject, translationKey?: TranslationsKey): void;
	public showPopOver(
		type: PopOverType,
		position: { x: number; y: number },
		translationKey?: TranslationsKey
	): void;
	public showPopOver(
		type: PopOverType,
		value: SceneObject | { x: number; y: number },
		translationKey?: TranslationsKey
	): void {
		let screenPosition: { x: number; y: number };
		const valueIsPosition = value['x'] && value['y'];
		if (valueIsPosition) {
			screenPosition = value as { x: number; y: number };
		} else {
			const object = value as SceneObject;
			this.setObjectsPopOverOffset(object);

			const { node } = object;
			if (!node) {
				throw 'UiController: showPopOver(): node does not exist';
			}
			const component = node.getComponent('MeshRenderer') as MeshRenderer;
			if (!component) {
				throw 'UiController: showPopOver(): MeshRenderer does not exist';
			}
			screenPosition = component.getScreenPosition(true);
		}
		const translation = translationKey ? translationKey : (type as TranslationsKey);
		if (screenPosition.y > 0 && screenPosition.x > 0) {
			this.props.showPopOver({
				type,
				top: screenPosition.y,
				left: screenPosition.x,
				translationKey: translation,
			});
		}
	}

	public hidePopOver(type: PopOverType): void {
		this.props.hidePopOver({ type });
	}

	public hideAllPopOvers(): void {
		this.props.hidePopOver({ type: 'middle-module-drag' });
		this.props.hidePopOver({ type: 'unfinished-section' });
		this.props.hidePopOver({ type: 'missing-cover-section' });
		this.props.hidePopOver({ type: 'invalid-placement' });
		this.props.hidePopOver({ type: 'no-valid-connection' });
		this.props.hidePopOver({ type: 'disabled-drag' });
		this.props.hidePopOver({ type: 'build-limit' });
		this.props.hidePopOver({ type: 'placement-information' });
		this.props.setShowSelectAll(false);
	}

	public setPopOverSceneItemData(sceneObject: SceneObject): void {
		const item = this.getPopOverSceneItemData(sceneObject);
		if (item) {
			if (item.articleId.includes('backrest')) {
				// Backrest selected, show menu for its parent instead, if not a chaise
				const parentSceneObject = this.getParentSceneObject(sceneObject);
				if (parentSceneObject) {
					const isChaise =
						this.connectionManager.getFamily(parentSceneObject.articleId) === 'CHAISE';
					if (!isChaise) {
						this.props.selectSceneItem(this.getPopOverSceneItemData(parentSceneObject));
					} else {
						this.props.selectSceneItem(item);
					}
				}
			} else {
				this.props.selectSceneItem(item);
			}
		} else {
			throw 'UiController: setPopOverSceneItemData(): item does not exist';
		}
	}

	public getParentSceneObject(sceneObject: SceneObject): SceneObject | null {
		const parentId = sceneObject.parentId;
		if (parentId) {
			const sceneItemsArr = Array.from(this.sceneItems.values());
			const sceneObject = sceneItemsArr.find((item) => item.id === parentId);
			return sceneObject || null;
		}
		return null;
	}

	public getPopOverSceneItemData(sceneObject: SceneObject): PopOverSceneItemData | null {
		const object =
			this.props.specificItems && this.props.specificItems.has(sceneObject.id)
				? this.getSpecificParent(this.props.specificItems)
				: sceneObject;
		this.setObjectsPopOverOffset(object);
		const { id, articleId } = object;
		if (object && object.node) {
			const component = object.node.getComponent('MeshRenderer') as MeshRenderer;
			if (!component) {
				return null;
			}
			const screenPosition = component.getScreenPosition(true);
			const disconnectable = this.logic.isDisconnectable(object);
			const rotatable = this.logic.isRotatable(object);
			const moduleName = this.props.moduleSwiperItems.find(
				(item) => item.id === articleId
			)?.productName;
			if (moduleName) {
				this.moduleName = moduleName;
			}
			return {
				id,
				articleId,
				screenPosition,
				disconnectable,
				rotatable,
				moduleName: this.moduleName,
			};
		}
		return null;
	}

	private setObjectsPopOverOffset(sceneObject: SceneObject): void {
		const node = sceneObject.node;
		const articleId = sceneObject.articleId;
		if (node) {
			const renderer = node.getComponent<MeshRenderer>('MeshRenderer');
			if (renderer) {
				const y = this.getPopOverOffset(articleId);
				renderer.state.screenOffset = { y };
			}
		} else {
			throw 'UiController: updateObjectsPopOverOffsets(): node does not exist';
		}
	}

	private getPopOverOffset(articleId: string): number {
		const isMobile = this.props.deviceType == 'mobile';
		const isPortrait = this.props.orientation == 'portrait';

		if (isMobile) {
			return isPortrait ? ITEM_POPOVER_OFFSET.mobilePortrait : ITEM_POPOVER_OFFSET.mobileLandscape;
		}

		const isHeadrest = this.connectionManager.getFamily(articleId) === 'HEADREST';
		return isHeadrest ? ITEM_POPOVER_OFFSET.headrest : ITEM_POPOVER_OFFSET.default;
	}

	private getSpecificParent(specificItems: Map<string, SceneObject>): SceneObject {
		const specificItemsArr = Array.from(specificItems.values());
		return specificItemsArr.filter((item) => !item.articleId.includes('backrest'))[0];
	}
}
